import { CircleHelp } from 'lucide-react';
import { Tooltip } from 'radix-ui';
import { useState, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

/**
 * Подсказка у значка «?» — или у своего триггера, переданного в `children`.
 *
 * На десктопе открывается наведением и фокусом, на телефоне — тапом: наведения
 * там нет, а подсказка нужна так же. Закрывается уводом курсора, тапом мимо
 * или Esc.
 */
export function HintIcon({
  text,
  children,
  className,
}: {
  text: string;
  children?: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root open={open} onOpenChange={setOpen}>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            aria-label={children ? undefined : 'Подсказка'}
            // Radix закрывает подсказку нажатием на триггер, а тапу нужно
            // обратное — его обработчики гасим через preventDefault.
            onPointerDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              setOpen(true);
            }}
            className={cn(
              'rounded outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30',
              !children &&
                'inline-grid size-5 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:text-slate-600',
              className
            )}
          >
            {children ?? <CircleHelp className="size-4" />}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            collisionPadding={12}
            className="z-[80] max-w-[280px] rounded-lg bg-slate-900 px-3 py-2 text-xs leading-relaxed font-normal text-white shadow-lg"
          >
            {text}
            <Tooltip.Arrow className="fill-slate-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
